// NPM Imports
import React, { Component } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modifier, EditorState } from "draft-js";

// Internal Imports
import { DRAFT, DOCUMENTS } from "../../constants";
import {
  updateSelectionForEntity,
  getClosestFontWeightStyle,
} from "../../utils/draftUtils";
import applyFontStyle from "../CustomRevvToolbar/applyFontStyle";

// Destructuring
const { TOOLBAR_HEADER_BUTTONS } = DRAFT;
const { FONT_FAMILY_OPTIONS, FONT_WEIGHT_OPTIONS, FONT_SIZE_OPTIONS } =
  DOCUMENTS;

// Declarations
const FONT_HEADING_STYLE_MAP = {
  FONTFAMILY: "FONTFAMILY",
  FONTWEIGHT: "FONTWEIGHT",
  FONTSIZE: "FONTSIZE",
  FONTCOLOR: "FONTCOLOR",
};

const DEFAULT_STYLES = {
  fontFamily: "FONTFAMILY_OPENSANS",
  fontSize: "FONTSIZE_FOURTEEN",
  fontColor: "FONTCOLOR_000000",
};

class HeaderButtonDropdown extends Component {
  state = {
    stylesAtFocus: null,
  };

  componentDidMount = () => {
    this._getStylesAtFocus();
  };

  _applyfontFamily = (editorState, fontStyle, fontValue, fontWeightStyle) => {
    let newEditorState = editorState;

    if (!fontWeightStyle) {
      // if no font weight, get current font weight & find closed one
      const currentInlineStyles = newEditorState.getCurrentInlineStyle();
      if (currentInlineStyles && currentInlineStyles.size > 0) {
        const oldFontWeight = currentInlineStyles.filter(style =>
          style.startsWith("FONTWEIGHT"),
        );
        if (oldFontWeight && oldFontWeight.size > 0) {
          const { newFontWeightStyle } = getClosestFontWeightStyle(
            [...oldFontWeight][0],
            fontStyle,
            fontValue,
          );
          fontWeightStyle = newFontWeightStyle;
        }
      }
    }

    // ADD NEW
    newEditorState = applyFontStyle(newEditorState, fontStyle, "FONTFAMILY");
    if (fontWeightStyle) {
      // remove bold
      const nextContentState = Modifier.removeInlineStyle(
        newEditorState.getCurrentContent(),
        newEditorState.getSelection(),
        "BOLD",
      );
      const selection = newEditorState.getSelection();
      let currentInlineStyle = newEditorState.getCurrentInlineStyle();
      newEditorState = EditorState.push(
        newEditorState,
        nextContentState,
        "change-inline-style",
      );
      if (selection && selection.isCollapsed()) {
        currentInlineStyle = currentInlineStyle.filter(
          style => style && style !== "BOLD",
        );
        newEditorState = EditorState.setInlineStyleOverride(
          newEditorState,
          currentInlineStyle,
        );
      }

      newEditorState = applyFontStyle(
        newEditorState,
        fontWeightStyle,
        "FONTWEIGHT",
      );
    }

    return newEditorState;
  };

  _applyInlineFontStyle = (editorState, buttonType, data) => {
    let updatedEditorState = editorState;
    /**
     * ####### Update selection for "DYNAMICFIELD" #######
     * here, need to update the selection state
     * if the anchor or focus or both positions have "DYNAMICFIELD",
     * so that if apply styles on one word of DF, it must apply on all words of DF
     */
    const updatedSelectionForEntity = updateSelectionForEntity(
      updatedEditorState,
      "DYNAMICFIELD",
    );
    if (updatedSelectionForEntity) {
      const newEditorState = EditorState.acceptSelection(
        updatedEditorState,
        updatedSelectionForEntity,
      );
      updatedEditorState = newEditorState;
    }
    if (buttonType === "FONTSIZE" || buttonType === "FONTCOLOR") {
      updatedEditorState = applyFontStyle(updatedEditorState, data, buttonType);
    }
    return updatedEditorState;
  };

  _applyFontHeadingRecentStyles = (event, styles) => {
    event.preventDefault();
    let updatedEditorState = this.props.editorState;
    if (Object.keys(styles).length > 0) {
      updatedEditorState = this.props.handlePageNumber(updatedEditorState);
      if (styles.fontFamily || styles.fontWeight) {
        updatedEditorState = this._applyfontFamily(
          updatedEditorState,
          styles.fontFamily,
          FONT_FAMILY_OPTIONS[styles.fontFamily].fontFamily,
          styles.fontWeight,
        );
      }
      if (styles.fontSize) {
        updatedEditorState = this._applyInlineFontStyle(
          updatedEditorState,
          FONT_HEADING_STYLE_MAP.FONTSIZE,
          styles.fontSize,
        );
      }
      if (styles.fontColor) {
        updatedEditorState = this._applyInlineFontStyle(
          updatedEditorState,
          FONT_HEADING_STYLE_MAP.FONTCOLOR,
          styles.fontColor,
        );
      }
    }
    this.props.handleChange(updatedEditorState);
    this.props.toogleToolbarDropDown(null);
  };

  // This method is being used here to handle the only eslint issue
  _getFontSize = fontSize => ({ fontSize });

  _getStylesAtFocus = () => {
    const { editorState } = this.props;
    const currentFocus = editorState.getSelection().getFocusKey();
    const inlineStyle = editorState.getCurrentInlineStyle(currentFocus);
    const styles = Array.from(inlineStyle);

    const fontHeadingStyle = {};
    if (Array.isArray(styles)) {
      styles.forEach(style => {
        if (
          style.includes(FONT_HEADING_STYLE_MAP.FONTFAMILY) &&
          !fontHeadingStyle.fontFamily
        ) {
          fontHeadingStyle.fontFamily = style;
        } else if (
          style.includes(FONT_HEADING_STYLE_MAP.FONTWEIGHT) &&
          !fontHeadingStyle.fontWeight
        ) {
          fontHeadingStyle.fontWeight = style;
        } else if (
          style.includes(FONT_HEADING_STYLE_MAP.FONTSIZE) &&
          !fontHeadingStyle.fontSize
        ) {
          fontHeadingStyle.fontSize = style;
        } else if (
          style.includes(FONT_HEADING_STYLE_MAP.FONTCOLOR) &&
          !fontHeadingStyle.fontColor
        ) {
          fontHeadingStyle.fontColor = style;
        }
      });
    }

    if (!fontHeadingStyle.fontFamily) {
      fontHeadingStyle.fontFamily = DEFAULT_STYLES.fontFamily;
    }
    if (!fontHeadingStyle.fontSize) {
      fontHeadingStyle.fontSize = DEFAULT_STYLES.fontSize;
    }
    if (!fontHeadingStyle.fontColor) {
      fontHeadingStyle.fontColor = DEFAULT_STYLES.fontColor;
    }
    if (
      fontHeadingStyle.fontFamily &&
      fontHeadingStyle.fontSize &&
      fontHeadingStyle.fontColor
    ) {
      this.setState({ stylesAtFocus: fontHeadingStyle });
    }
  };

  _addCustomStyle = event => {
    event.preventDefault();
    this._saveMetaData(this.state.stylesAtFocus);
  };

  _saveMetaData = currentStyles => {
    // update font heading styles in recent styles
    const updateRecentStyles = this.props.metaData
      ? { ...this.props.metaData.recentStyles }
      : {};
    if (this.props.allowToSaveRecentStyles) {
      if (
        this.props.metaData &&
        this.props.metaData.recentStyles &&
        this.props.metaData.recentStyles.fontHeadingStyles &&
        this.props.metaData.recentStyles.fontHeadingStyles.length > 0
      ) {
        const { fontHeadingStyles } = this.props.metaData.recentStyles;
        const tempFontHeadingStyles = [...fontHeadingStyles];
        const findIndex = tempFontHeadingStyles.findIndex(fontStyle => {
          if (
            fontStyle.fontWeight &&
            fontStyle.fontWeight === currentStyles.fontWeight
          ) {
            return (
              fontStyle.fontFamily === currentStyles.fontFamily &&
              fontStyle.fontWeight === currentStyles.fontWeight &&
              fontStyle.fontSize === currentStyles.fontSize &&
              fontStyle.fontColor === currentStyles.fontColor
            );
          }
          return (
            fontStyle.fontFamily === currentStyles.fontFamily &&
            fontStyle.fontSize === currentStyles.fontSize &&
            fontStyle.fontColor === currentStyles.fontColor
          );
        });

        if (findIndex === -1) {
          tempFontHeadingStyles.push(currentStyles);
          updateRecentStyles.fontHeadingStyles = [...tempFontHeadingStyles];
        }
      } else {
        updateRecentStyles.fontHeadingStyles = [currentStyles];
      }
    }

    const data = [
      {
        op: "replace",
        field: "meta_data",
        value: {
          meta_data: {
            ...this.props.metaData,
            recentStyles: updateRecentStyles,
          },
        },
      },
    ];

    this.props.saveMetaData(data);
  };

  _getFontHeadingStyles = fontHeadingStyle => {
    const styles = {};
    if (
      fontHeadingStyle.fontFamily &&
      FONT_FAMILY_OPTIONS[fontHeadingStyle.fontFamily]
    ) {
      styles.fontFamily =
        FONT_FAMILY_OPTIONS[fontHeadingStyle.fontFamily].fontFamily;
    }
    if (
      fontHeadingStyle.fontWeight &&
      FONT_WEIGHT_OPTIONS[fontHeadingStyle.fontWeight]
    ) {
      styles.fontWeight =
        FONT_WEIGHT_OPTIONS[fontHeadingStyle.fontWeight].fontWeight;
    }
    if (fontHeadingStyle.fontColor) {
      styles.color = `#${fontHeadingStyle.fontColor.split("_")[1]}`;
    }

    return styles;
  };

  render() {
    let isRecentFontHeadingAvailable = false;
    if (
      this.props.metaData &&
      this.props.metaData.recentStyles &&
      this.props.metaData.recentStyles.fontHeadingStyles &&
      this.props.metaData.recentStyles.fontHeadingStyles.length > 0
    ) {
      isRecentFontHeadingAvailable = true;
    }
    return (
      <div className="headerbuttons-style-wrapper">
        <ul className="headerbuttons-style-options">
          <li key={"standardStyleLabel"} className={"font-style-label"}>
            Standard styles
          </li>

          {TOOLBAR_HEADER_BUTTONS.map(btn => (
            <li
              key={btn.type}
              // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
              role="button"
              tabIndex={0}
              onMouseDown={event => this.props.onChange(event, btn.type)}
              style={this._getFontSize(btn.fontSize)}>
              <div
                className={`headerbuttons-style-option ${
                  this.props.activeStatus === btn.value ? "active" : ""
                }`}>
                {btn.value}
              </div>
            </li>
          ))}

          {isRecentFontHeadingAvailable && (
            <li
              key={"standardFontLabel"}
              className={"font-style-label top-border"}>
              Custom styles
            </li>
          )}

          {isRecentFontHeadingAvailable &&
            this.props.metaData.recentStyles.fontHeadingStyles.map(
              (headingStyle, index) => (
                <li
                  // eslint-disable-next-line react/no-array-index-key
                  key={`fontHeadingStyle-${index}`}
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
                  role="button"
                  tabIndex={0}
                  onMouseDown={event =>
                    this._applyFontHeadingRecentStyles(event, headingStyle)
                  }>
                  <div className={"headerbuttons-style-option"}>
                    <div
                      className={"font-heading-style-label"}
                      style={this._getFontHeadingStyles(headingStyle)}>
                      {`Custom Style ${index + 1}`}{" "}
                    </div>
                    <div className={"font-heading-style-label-row"}>
                      {FONT_FAMILY_OPTIONS[headingStyle.fontFamily] && (
                        <span className={"font-heading-style-label sub-label"}>
                          {`${
                            FONT_FAMILY_OPTIONS[headingStyle.fontFamily]
                              .fontFamily
                          } ${!headingStyle.fontWeight ? " ," : ""}`}{" "}
                          {headingStyle.fontWeight ? (
                            <span
                              className={"font-heading-style-label sub-label"}>
                              {` - ${
                                FONT_WEIGHT_OPTIONS[headingStyle.fontWeight]
                                  .fontWeightName
                              } ,`}
                            </span>
                          ) : null}{" "}
                        </span>
                      )}
                      {FONT_SIZE_OPTIONS[headingStyle.fontSize] && (
                        <span className={"font-heading-style-label sub-label"}>
                          {`${
                            FONT_SIZE_OPTIONS[headingStyle.fontSize].fontSize
                          }, `}
                        </span>
                      )}
                      {headingStyle.fontColor && (
                        <span className={"font-heading-style-label sub-label"}>
                          {`#${headingStyle.fontColor.split("_")[1]}`}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              ),
            )}

          {this.props.allowToSaveRecentStyles && (
            <li
              key={"addCustomStyle"}
              // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
              role="button"
              tabIndex={0}
              onMouseDown={
                this.state.stylesAtFocus ? this._addCustomStyle : () => {}
              }
              className={`add-custom-style-list-item ${
                !this.state.stylesAtFocus ? " disabled " : ""
              }`}>
              <div
                className={
                  "headerbuttons-style-option add-custom-style-button"
                }>
                <FontAwesomeIcon
                  icon={["far", "plus"]}
                  className="add-custom-style-plus-icon"
                />{" "}
                Add custom style
              </div>
            </li>
          )}
        </ul>
      </div>
    );
  }
}

export default HeaderButtonDropdown;
